import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebase/config.js';
import { doc, getDoc, collection, query, where, getDocs, addDoc, serverTimestamp } from 'firebase/firestore';

const DynamicVolunteerRegistration = () => {
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);
  const [tenantInfo, setTenantInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [accessCode, setAccessCode] = useState('');
  const [accessGranted, setAccessGranted] = useState(false);

  useEffect(() => {
    loadConfiguration();
  }, []);

  const loadConfiguration = async () => {
    try {
      // Extract tenant subdomain from hostname
      const subdomain = window.location.hostname.split('.')[0];
      console.log('Extracted subdomain:', subdomain);

      if (!subdomain || subdomain === 'localhost') {
        setMessage('Invalid registration link');
        setLoading(false);
        return;
      }

      // Find tenant by subdomain
      const tenantsQuery = query(
        collection(db, 'tenants'), 
        where('subdomain', '==', subdomain)
      );
      const tenantsSnapshot = await getDocs(tenantsQuery);
      
      if (tenantsSnapshot.empty) {
        console.error('No tenant found for subdomain:', subdomain);
        setMessage('Invalid registration link');
        setLoading(false);
        return;
      }

      const tenantDoc = tenantsSnapshot.docs[0];
      const tenantData = tenantDoc.data();
      const tenantId = tenantDoc.id;
      
      console.log('Found tenant:', tenantData);
      setTenantInfo(tenantData);

      // Look for volunteer registration configuration
      const configQuery = query(
        collection(db, 'tenantConfigurations'),
        where('tenantId', '==', tenantId),
        where('configKey', '==', 'volunteer_registration')
      );
      const configSnapshot = await getDocs(configQuery);

      if (configSnapshot.empty) {
        console.error('No volunteer registration config found for tenant:', tenantId);
        setMessage('Registration form not configured');
        setLoading(false);
        return;
      }

      const configDoc = configSnapshot.docs[0];
      const formConfig = configDoc.data().configValue;
      
      console.log('VOLUNTEER CONFIG LOADED:', formConfig);
      setConfig({ ...formConfig, tenantId }); // Include tenantId in config
      
      // Check if access code is required
      if (!formConfig.settings?.requireAccessCode) {
        setAccessGranted(true);
      }

    } catch (error) {
      console.error('Error loading configuration:', error);
      setMessage('Error loading registration form');
    }
    setLoading(false);
  };

  const handleAccessCodeSubmit = () => {
    if (accessCode === config.settings.accessCode) {
      setAccessGranted(true);
      setMessage('');
    } else {
      setMessage('Invalid access code');
    }
  };

  const handleInputChange = (fieldKey, value) => {
    setFormData(prev => ({
      ...prev,
      [fieldKey]: value
    }));
  };

  const handleMultiSelectChange = (fieldKey, value, checked) => {
    setFormData(prev => {
      const currentValues = prev[fieldKey] || [];
      if (checked) {
        return {
          ...prev,
          [fieldKey]: [...currentValues, value]
        };
      } else {
        return {
          ...prev,
          [fieldKey]: currentValues.filter(v => v !== value)
        };
      }
    });
  };

  const validateForm = () => {
    const errors = [];
    
    // Check required fields in each enabled section
    Object.entries(config.sections || {}).forEach(([sectionKey, section]) => {
      if (section.enabled) {
        Object.entries(section.fields || {}).forEach(([fieldKey, field]) => {
          if (field.enabled && field.required) {
            const value = formData[fieldKey];
            if (!value || (Array.isArray(value) && value.length === 0)) {
              errors.push(`${field.label} is required`);
            }
          }
        });
      }
    });

    // Check custom fields
    config.customFields?.forEach(field => {
      if (field.enabled && field.required) {
        const value = formData[field.id];
        if (!value || (Array.isArray(value) && value.length === 0)) {
          errors.push(`${field.label} is required`);
        }
      }
    });

    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setMessage('');

    const errors = validateForm();
    if (errors.length > 0) {
      setMessage(errors.join(', '));
      setSubmitting(false);
      return;
    }

    try {
      const registrationData = {
        ...formData,
        tenantId: config.tenantId,
        status: config.settings?.requireApproval ? 'pending' : 'approved',
        submittedAt: serverTimestamp(),
        registrationType: 'volunteer'
      };

      console.log('Submitting volunteer registration data:', registrationData);
      await addDoc(collection(db, 'volunteerRegistrations'), registrationData);
      
      setMessage('Registration submitted successfully! You will be notified when reviewed.');
      setFormData({});
    } catch (error) {
      console.error('Error submitting registration:', error);
      setMessage('Error submitting registration. Please try again.');
    }
    
    setSubmitting(false);
  };

  const renderField = (fieldKey, field) => {
    const value = formData[fieldKey] || '';
    
    switch (field.type) {
      case 'text':
      case 'email':
      case 'tel':
      case 'number':
      case 'date':
        return (
          <input
            type={field.type}
            value={value}
            onChange={(e) => handleInputChange(fieldKey, e.target.value)}
            placeholder={field.label}
            required={field.required}
            style={{
              width: '100%',
              padding: '12px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              fontSize: '16px',
              boxSizing: 'border-box'
            }}
          />
        );

      case 'textarea':
        return (
          <textarea
            value={value}
            onChange={(e) => handleInputChange(fieldKey, e.target.value)}
            placeholder={field.label}
            required={field.required}
            rows={4}
            style={{
              width: '100%',
              padding: '12px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              fontSize: '16px',
              resize: 'vertical',
              boxSizing: 'border-box'
            }}
          />
        );

      case 'select':
        return (
          <select
            value={value}
            onChange={(e) => handleInputChange(fieldKey, e.target.value)}
            required={field.required}
            style={{
              width: '100%',
              padding: '12px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              fontSize: '16px',
              boxSizing: 'border-box'
            }}
          >
            <option value="">Please select...</option>
            {field.options?.map(option => (
              <option key={option} value={option}>{option}</option>
            ))}
          </select>
        );

      case 'multiselect':
        const selectedValues = formData[fieldKey] || [];
        return (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '10px' }}>
            {field.options?.map(option => (
              <label key={option} style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', padding: '8px', border: '1px solid #e0e0e0', borderRadius: '4px' }}>
                <input
                  type="checkbox"
                  checked={selectedValues.includes(option)}
                  onChange={(e) => handleMultiSelectChange(fieldKey, option, e.target.checked)}
                />
                <span style={{ fontSize: '14px' }}>{option}</span>
              </label>
            ))}
          </div>
        );

      default:
        return null;
    }
  };

  const renderSection = (sectionKey, sectionTitle, sectionDescription) => {
    const section = config.sections?.[sectionKey];
    if (!section?.enabled) return null;

    // Get all available field keys from the actual configuration
    const availableFields = Object.keys(section.fields || {});
    
    // Define priority orders for common fields (when they exist)
    const fieldPriorities = {
      // Personal Info priority order
      firstName: 1, lastName: 2, email: 3, phone: 4, address: 5, city: 6, state: 7, zip: 8,
      // Emergency priority order  
      emergencyName: 1, emergencyPhone: 2, emergencyRelation: 3,
      // Skills priority order
      skills: 1, experience: 2,
      // Interests priority order
      interests: 1,
      // Availability priority order
      availableWeekdays: 1, availableWeekends: 2, frequency: 3,
      // Additional priority order
      referralSource: 1, additionalInfo: 2
    };

    // Sort fields: prioritized ones first, then alphabetical for the rest
    const sortedFields = availableFields.sort((a, b) => {
      const priorityA = fieldPriorities[a] || 999;
      const priorityB = fieldPriorities[b] || 999;
      
      if (priorityA !== priorityB) {
        return priorityA - priorityB;
      }
      
      // If both have same priority (or no priority), sort alphabetically
      return a.localeCompare(b);
    });

    return (
      <div key={sectionKey} style={{ marginBottom: '40px' }}>
        <h3 style={{ color: '#333', marginBottom: '10px', fontSize: '20px' }}>{sectionTitle}</h3>
        <p style={{ color: '#666', marginBottom: '20px', fontSize: '14px' }}>{sectionDescription}</p>
        
        {sortedFields.map((fieldKey) => {
          const field = section.fields[fieldKey];
          if (!field?.enabled) return null;
          return (
            <div key={fieldKey} style={{ marginBottom: '20px' }}>
              <label style={{ 
                display: 'block', 
                marginBottom: '8px', 
                fontWeight: '500',
                color: '#333',
                fontSize: '14px'
              }}>
                {field.label}
                {field.required && <span style={{ color: '#dc3545' }}> *</span>}
              </label>
              {renderField(fieldKey, field)}
            </div>
          );
        })}
      </div>
    );
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f8f9fa' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '18px', color: '#666' }}>Loading registration form...</div>
        </div>
      </div>
    );
  }

  if (!config || message === 'Invalid registration link' || message === 'Registration form not configured') {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f8f9fa' }}>
        <div style={{ textAlign: 'center', maxWidth: '500px', padding: '40px' }}>
          <h2 style={{ color: '#dc3545', marginBottom: '20px' }}>Registration Unavailable</h2>
          <p style={{ color: '#666' }}>{message || 'Registration form could not be loaded.'}</p>
        </div>
      </div>
    );
  }

  // Access code gate
  if (config.settings?.requireAccessCode && !accessGranted) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#f8f9fa', padding: '40px 20px' }}>
        <div style={{ maxWidth: '500px', margin: '0 auto', backgroundColor: 'white', padding: '40px', borderRadius: '8px', boxShadow: '0 2px 10px rgba(0,0,0,0.1)' }}>
          <div style={{ textAlign: 'center', marginBottom: '30px' }}>
            <h1 style={{ margin: '0 0 10px 0', color: '#333' }}>Volunteer Registration</h1>
            <p style={{ margin: 0, color: '#666' }}>
              {tenantInfo?.organizationName || tenantInfo?.name || 'Organization'}
            </p>
            <div style={{ fontSize: '48px', marginBottom: '20px' }}>🔐</div>
            <h3 style={{ color: '#333', marginBottom: '15px' }}>Access Code Required</h3>
            <p style={{ color: '#666', lineHeight: '1.6' }}>
              Please enter the access code to continue with your volunteer registration.
            </p>
          </div>
          
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: '#333' }}>
              Access Code
            </label>
            <input
              type="text"
              value={accessCode}
              onChange={(e) => setAccessCode(e.target.value)}
              placeholder="Enter access code"
              style={{
                width: '100%',
                padding: '12px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '16px',
                textAlign: 'center',
                letterSpacing: '1px',
                boxSizing: 'border-box'
              }}
            />
          </div>

          {message && (
            <div style={{ 
              color: '#dc3545', 
              textAlign: 'center', 
              marginBottom: '20px',
              backgroundColor: '#f8d7da',
              padding: '12px',
              borderRadius: '4px'
            }}>
              {message}
            </div>
          )}

          <button
            onClick={handleAccessCodeSubmit}
            style={{
              width: '100%',
              padding: '12px',
              backgroundColor: '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              fontSize: '16px',
              fontWeight: 'bold',
              cursor: 'pointer'
            }}
          >
            Continue to Registration
          </button>
        </div>
      </div>
    );
  }

  // Main form
  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f8f9fa', padding: '40px 20px' }}>
      <div style={{ maxWidth: '800px', margin: '0 auto', backgroundColor: 'white', borderRadius: '8px', boxShadow: '0 2px 10px rgba(0,0,0,0.1)' }}>
        
        {/* Header */}
        <div style={{ padding: '30px 40px', borderBottom: '1px solid #eee', textAlign: 'center' }}>
          <h1 style={{ margin: '0 0 10px 0', color: '#333' }}>Volunteer Registration</h1>
          <p style={{ textAlign: 'center', color: '#666', marginBottom: '30px' }}>
            {tenantInfo?.organizationName || tenantInfo?.name || 'Organization'}
          </p>
          {config.settings?.welcomeMessage && (
            <p style={{ color: '#666', marginTop: '10px', margin: '10px 0 0 0' }}>{config.settings.welcomeMessage}</p>
          )}
        </div>

        <form onSubmit={handleSubmit} style={{ padding: '40px' }}>
          
          {/* HARDCODED SECTION ORDER - NO MORE RANDOMNESS */}
          {renderSection('personalInfo', 'Personal Information', 'Basic contact and personal details')}
          
          {renderSection('emergency', 'Emergency Contact', 'Emergency contact information')}
          
          {renderSection('skills', 'Skills & Experience', 'What skills and experience do you have?')}
          
          {renderSection('interests', 'Areas of Interest', 'What volunteer opportunities interest you?')}
          
          {renderSection('availability', 'Availability', 'When are you available to volunteer?')}
          
          {renderSection('additional', 'Additional Information', 'Any other information you would like to share')}

          {/* Custom fields */}
          {config.customFields?.length > 0 && (
            <div style={{ marginBottom: '40px' }}>
              <h3 style={{ color: '#333', marginBottom: '20px', fontSize: '20px' }}>Additional Information</h3>
              {config.customFields.map(field => {
                if (!field.enabled) return null;
                
                return (
                  <div key={field.id} style={{ marginBottom: '20px' }}>
                    <label style={{ 
                      display: 'block', 
                      marginBottom: '8px', 
                      fontWeight: '500',
                      color: '#333',
                      fontSize: '14px'
                    }}>
                      {field.label}
                      {field.required && <span style={{ color: '#dc3545' }}> *</span>}
                    </label>
                    {renderField(field.id, field)}
                  </div>
                );
              })}
            </div>
          )}

          {/* Background check consent */}
          {config.settings?.requireBackgroundCheck && (
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'flex', alignItems: 'flex-start', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={formData.backgroundCheckConsent || false}
                  onChange={(e) => handleInputChange('backgroundCheckConsent', e.target.checked)}
                  required
                  style={{ marginRight: '10px', marginTop: '3px' }}
                />
                <span style={{ fontSize: '14px', lineHeight: '1.5' }}>
                  I understand that a background check may be required for certain volunteer positions and I consent to such a check if needed.
                </span>
              </label>
            </div>
          )}

          {/* Terms and conditions */}
          {config.settings?.termsAndConditions && (
            <div style={{ marginBottom: '30px' }}>
              <label style={{ display: 'flex', alignItems: 'flex-start', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={formData.termsAccepted || false}
                  onChange={(e) => handleInputChange('termsAccepted', e.target.checked)}
                  required
                  style={{ marginRight: '10px', marginTop: '3px' }}
                />
                <span style={{ fontSize: '14px', lineHeight: '1.5' }}>
                  {config.settings.termsAndConditions}
                </span>
              </label>
            </div>
          )}

          {message && (
            <div style={{ 
              marginBottom: '20px', 
              padding: '12px', 
              backgroundColor: message.includes('success') || message.includes('submitted') ? '#d4edda' : '#f8d7da',
              color: message.includes('success') || message.includes('submitted') ? '#155724' : '#721c24',
              borderRadius: '4px',
              fontSize: '14px'
            }}>
              {message}
            </div>
          )}

          <button
            type="submit"
            disabled={submitting}
            style={{
              width: '100%',
              padding: '15px',
              backgroundColor: submitting ? '#6c757d' : '#28a745',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              fontSize: '16px',
              fontWeight: '500',
              cursor: submitting ? 'not-allowed' : 'pointer'
            }}
          >
            {submitting ? 'Submitting...' : 'Submit Registration'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default DynamicVolunteerRegistration;